import { z } from 'zod';

/**
 * 原始记录池的对外契约。
 *
 * **两个时间维度是契约的一部分，不是实现细节**（ADR-0039 三）：
 * `uploadedAt` 导入时间、`contentTime` 资料本身的时间，外加 `contentTimeSource`
 * 时间的出处。少任何一个，前端就只能把两件事画成一件。
 */

export const carrierKindSchema = z.enum([
  'conversation',
  'spreadsheet',
  'document',
  'drawing',
  'formal_document',
  'image',
  'archive',
]);
export type CarrierKind = z.infer<typeof carrierKindSchema>;

export const provenanceSchema = z.enum(['client_provided', 'provider_produced']);
export type Provenance = z.infer<typeof provenanceSchema>;

/** 时间的出处。**强弱有别，前端要按它决定显示成事实还是推断。** */
export const contentTimeSourceSchema = z.enum([
  'exif',
  'office_meta',
  'filename',
  'file_mtime',
  'embedded_doc',
]);
export type ContentTimeSource = z.infer<typeof contentTimeSourceSchema>;

export const timeAxisSchema = z.enum(['uploaded', 'content']);
export type TimeAxis = z.infer<typeof timeAxisSchema>;

export const materialRecordSchema = z.object({
  materialId: z.string(),
  originalName: z.string().nullable(),
  sourcePath: z.string().nullable(),
  mediaType: z.string(),
  carrierKind: z.string(),
  provenance: z.string(),
  byteSize: z.number(),
  contentHash: z.string(),
  uploadedBy: z.string(),
  /** 导入时间。 */
  uploadedAt: z.string(),
  /** 资料本身的时间。判不出来就是 null——**不拿导入时间充数**。 */
  contentTime: z.string().nullable(),
  contentTimeSource: contentTimeSourceSchema.nullable(),
  batchId: z.string().nullable(),
  batchLabel: z.string().nullable(),
  batchChannel: z.string().nullable(),
  /** 若它是从文档里提出来的图片：出自哪份、在哪个位置。 */
  sourceMaterialId: z.string().nullable(),
  sourceName: z.string().nullable(),
  sourceLocator: z.string().nullable(),
  /** 它自己带出来几张图。 */
  derivedCount: z.number(),
  /** 它出现在几份文档里（同一张截图可以出现在多份里）。 */
  derivedFromCount: z.number(),
  /**
   * 这份内容还以哪些名字被提交过。
   *
   * 去重命中时本体不插新行，名字永远停在第一次那个——而第一次可能是从文档里提出来的
   * `image1.png`，后来单独提交的 `微信图片_20260525103723_4_1899.png` 才有信息量。
   */
  alsoNamed: z.array(z.string()),
});
export type MaterialRecord = z.infer<typeof materialRecordSchema>;

export const timelinePageSchema = z.object({
  rows: z.array(materialRecordSchema),
  total: z.number(),
});
export type TimelinePage = z.infer<typeof timelinePageSchema>;

export const derivedRecordSchema = z.object({
  materialId: z.string(),
  originalName: z.string().nullable(),
  mediaType: z.string(),
  byteSize: z.number(),
  ordinal: z.number(),
  locator: z.string().nullable(),
  entryPath: z.string(),
  referenced: z.boolean(),
  alsoIn: z.number(),
});
export type DerivedRecord = z.infer<typeof derivedRecordSchema>;

export const derivedListSchema = z.object({ rows: z.array(derivedRecordSchema) });

/** 台账一行 = 一次导入。 */
export const ingestBatchSchema = z.object({
  batchId: z.string(),
  actor: z.string(),
  channel: z.string(),
  label: z.string().nullable(),
  sourceRoot: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  fileCount: z.number(),
  newCount: z.number(),
  dedupCount: z.number(),
  failedCount: z.number(),
  unsupportedCount: z.number(),
  byteCount: z.number(),
  mediaFound: z.number(),
  mediaNew: z.number(),
});
export type IngestBatch = z.infer<typeof ingestBatchSchema>;

export const ledgerPageSchema = z.object({
  rows: z.array(ingestBatchSchema),
  total: z.number(),
});
export type LedgerPage = z.infer<typeof ledgerPageSchema>;

export const ingestItemSchema = z.object({
  itemId: z.string(),
  submittedName: z.string(),
  sourcePath: z.string().nullable(),
  byteSize: z.number(),
  outcome: z.enum(['stored', 'deduplicated', 'failed']),
  extraction: z.enum(['not_applicable', 'extracted', 'unsupported', 'failed']),
  materialId: z.string().nullable(),
  mediaFound: z.number(),
  mediaNew: z.number(),
  failureReason: z.string().nullable(),
  ingestedAt: z.string(),
});
export type IngestItem = z.infer<typeof ingestItemSchema>;

export const ingestItemListSchema = z.object({ rows: z.array(ingestItemSchema) });

/** 开一批。`label` 是「这批是什么」，台账上给人看的那一栏。 */
export const openBatchSchema = z.object({
  label: z.string().trim().max(200).optional(),
  provenance: provenanceSchema,
  /**
   * 这一批是为哪张 ATC 收的。执行页的上传区带它，记录池直接上传时不带。
   *
   * **它只多写一条登记，不另开一条上传路径**（ADR-0039 二）。
   */
  atcCode: z.string().trim().max(64).optional(),
});
export type OpenBatch = z.infer<typeof openBatchSchema>;

export const batchOpenedSchema = z.object({ batchId: z.string() });

/** 一份文件的入库结果，逐份返回给前端做进度与结果显示。 */
export const ingestResultSchema = z.object({
  itemId: z.string(),
  submittedName: z.string(),
  outcome: z.enum(['stored', 'deduplicated', 'failed']),
  extraction: z.enum(['not_applicable', 'extracted', 'unsupported', 'failed']),
  materialId: z.string().nullable(),
  byteSize: z.number(),
  carrierKind: z.string(),
  contentTime: z.string().nullable(),
  contentTimeSource: contentTimeSourceSchema.nullable(),
  mediaFound: z.number(),
  mediaNew: z.number(),
  failureReason: z.string().nullable(),
});
export type IngestResult = z.infer<typeof ingestResultSchema>;

export const poolSummarySchema = z.object({
  total: z.number(),
  derived: z.number(),
  /** 判出了「资料本身的时间」的份数。判不出的那些只有导入时间。 */
  withContentTime: z.number(),
  byCarrier: z.array(z.object({ carrierKind: z.string(), count: z.number() })),
  byProvenance: z.array(z.object({ provenance: z.string(), count: z.number() })),
});
export type PoolSummary = z.infer<typeof poolSummarySchema>;
